import type {
  FlowGeneratorSession,
  FlowGeneratorSessionIntervention,
  FlowItem,
} from "@/types/flow.ts";
import { None, type Option, Some } from "ts-results-es";
import { create } from "zustand/react";
import type { SocratesSession } from "@/types/socrates.ts";
import { log } from "@/helpers/console.ts";
import { ADT } from "pb.adt";
import { flowSessionQueryKey } from "@/helpers/flowSession.ts";
import type { HeldItem, HeldReplyReview } from "@/api/chat.ts";
import DOMPurify from "dompurify";

export const PollingStates = ADT.define(
  {} as {
    Idle: {};
    Fetching: { attempts: number };
    Ready: {};
    Failed: { reason: string };
  },
);

export const HeldReplyStates = ADT.define(
  {} as {
    none: {};
    unknown: {};
    held: {
      // Combined identity of everything held — any change means new/updated
      // content (superseded), the signal to reset review UI
      identity: string;
      // Post-eval hold: the AI's withheld draft (editable on approve)
      reply?: HeldItem;
      // Pre-eval hold: the student's flagged message (NEVER editable — the
      // server 400s an editedMessage while this is present)
      student?: HeldItem;
    };
    resolved: {
      review: HeldReplyReview;
      // Same identity scheme as "held" — lets the UI ignore a stale re-read of
      // an item it already knows was resolved (the backend can briefly serve
      // the pre-approve payload again)
      identity: string;
      message?: string;
    };
  },
);

export type HeldReplyState = ADT.define<typeof HeldReplyStates>;
export type PollingState = ADT.define<typeof PollingStates>;

export interface ChatSocrates {
  session?: SocratesSession;
  state: PollingState;
}

export interface FlaggedMessage {
  flagged?: HeldReplyState;
  state: PollingState;
}

export const DefaultChat: Chat = Object.freeze({
  flowGeneratorSessionId: 0,
  newMessages: [],
  removeHookCallbacks: [],
  unreadCount: 0,
  isStartupFinished: false,
  socrates: {
    session: undefined,
    state: PollingStates.Idle(),
  },
  llmMessage: undefined,
  hasBeenOpened: false,
  lastOpened: undefined,
  flaggedMessage: {
    flagged: undefined,
    state: PollingStates.Idle(),
  },
});

export interface Chat {
  flowGeneratorSessionId: number;
  flowGeneratorSession?: FlowGeneratorSession;
  flowGeneratorSessionIntervention?: FlowGeneratorSessionIntervention;
  newMessages: Array<FlowItem>;
  removeHookCallbacks: Array<Option<() => void>>;
  unreadCount: number;
  isStartupFinished: boolean;
  socrates: ChatSocrates;
  llmMessage?: string;
  hasBeenOpened: boolean;
  lastOpened?: Date;
  flaggedMessage: FlaggedMessage;
}

export type UseChatStore = {
  chats: Map<number, Chat>;
  activeChat: Option<number>;

  AddNewChat: (chat: Partial<Chat>) => void;
  RemoveChat: (flowGeneratorSessionId: number) => void;

  IncrementUnreadCounter: (flowGeneratorSessionId: number) => void;
  ResetUnreadCounter: (flowGeneratorSessionId: number) => void;

  /**
   * Soft reset the chat state for a given flow generator session.
   * This will clear the chat hooks, set socrates and finished bool to false, and requery flow-session data
   *  @param flowGeneratorSessionId number - The ID of the flow generator session to reset
   */
  SoftResetChat: (flowGeneratorSessionId: number) => void;

  /**
   * Prepend a new message to the chat for a given flow generator session.
   * This will add the message to the beginning of the chat messages array.
   * @param flowGeneratorSessionId number - The ID of the flow generator session to prepend the message to
   * @param message FlowItem - The message to prepend to the chat
   */
  PrependNewMessage: (flowGeneratorSessionId: number, message: FlowItem) => void;
  AppendHookCallback: (
    flowGeneratorSessionId: number,
    callbacks: Array<Option<() => void>>,
  ) => void;
  UnhookFromChat: (flowGeneratorSessionId: number) => void;

  SetChatActive: (flowGeneratorSessionId: number | null) => void;
  SetFlowGeneratorSession: (
    flowGeneratorSessionId: number,
    flowGeneratorSession: Partial<FlowGeneratorSession>,
  ) => void;
  SetFlowGeneratorSessionIntervention: (
    flowGeneratorSessionId: number,
    flowGeneratorSessionIntervention: FlowGeneratorSessionIntervention,
  ) => void;
  SetStartupFinished: (flowGeneratorSessionId: number) => void;
  SetSocratesState: (flowGeneratorSessionId: number, newState: PollingState) => void;
  SetLlmMessage: (flowGeneratorSessionId: number, message: string | undefined) => void;
  SetSocratesSession: (flowGeneratorSessionId: number, socratesSession: SocratesSession) => void;
  SetChatHasBeenOpened: (flowGeneratorSessionId: number) => void;
  SetFlaggedMessage: (flowGeneratorSessionId: number, held: HeldReplyState) => void;
  SetFlaggedMessageState: (flowGeneratorSessionId: number, state: PollingState) => void;
};

export const useChatStore = create<UseChatStore>((set) => ({
  chats: new Map<number, Chat>(),
  activeChat: None,

  AddNewChat: (chat: Partial<Chat>) =>
    set((state) => {
      const newChat = { ...DefaultChat, ...chat };
      if (state.chats.has(newChat.flowGeneratorSessionId)) {
        return { state };
      }

      if (!chat.lastOpened) chat.lastOpened = new Date();

      const chats = new Map(state.chats);
      chats.set(newChat.flowGeneratorSessionId, newChat);
      return { chats };
    }),

  RemoveChat: (flowGeneratorSessionId: number) =>
    set((state) => {
      if (!state.chats.has(flowGeneratorSessionId)) return state;

      const existing = state.chats.get(flowGeneratorSessionId);
      existing?.removeHookCallbacks.forEach((cb) => cb.unwrapOr(() => {})());

      const chats = new Map(state.chats);
      chats.delete(flowGeneratorSessionId);

      return { chats };
    }),

  IncrementUnreadCounter: (flowGeneratorSessionId: number) =>
    set((state) => {
      const existing = state.chats.get(flowGeneratorSessionId);
      if (!existing) return state;

      const chats = new Map(state.chats);
      chats.set(flowGeneratorSessionId, {
        ...existing,
        unreadCount: existing.unreadCount + 1,
      });
      return { chats };
    }),

  ResetUnreadCounter: (flowGeneratorSessionId: number) =>
    set((state) => {
      const existing = state.chats.get(flowGeneratorSessionId);
      if (!existing) return state;

      const chats = new Map(state.chats);
      chats.set(flowGeneratorSessionId, {
        ...existing,
        unreadCount: 0,
      });
      return { chats };
    }),

  PrependNewMessage: (flowGeneratorSessionId: number, message: FlowItem) =>
    set((state) => {
      const existing = state.chats.get(flowGeneratorSessionId);
      if (!existing) return state;

      // SignalR can redeliver an item (reconnect replay, duplicate hooks) —
      // the same flowItemId must not stack in the transcript
      if (
        message.flowItemId &&
        existing.newMessages.some(
          (existingMessage) => existingMessage.flowItemId === message.flowItemId,
        )
      )
        return state;

      message.text = DOMPurify.sanitize(message.text);

      const chats = new Map(state.chats);
      chats.set(flowGeneratorSessionId, {
        ...existing,
        newMessages: [message, ...existing.newMessages],
      });
      return { chats };
    }),

  AppendHookCallback: (flowGeneratorSessionId: number, callbacks: Array<Option<() => void>>) =>
    set((state) => {
      const existing = state.chats.get(flowGeneratorSessionId);
      if (!existing) return state;

      const chats = new Map(state.chats);
      chats.set(flowGeneratorSessionId, {
        ...existing,
        removeHookCallbacks: [...existing.removeHookCallbacks, ...callbacks],
      });
      return { chats };
    }),

  UnhookFromChat: (flowGeneratorSessionId: number) =>
    set((state) => {
      const existing = state.chats.get(flowGeneratorSessionId);
      if (!existing) return state;

      existing?.removeHookCallbacks.forEach((cb) => cb.unwrapOr(() => {})());

      const chats = new Map(state.chats);
      chats.set(flowGeneratorSessionId, {
        ...existing,
        removeHookCallbacks: [],
      });
      return { chats };
    }),

  SetChatActive: (flowGeneratorSessionId: number | null) =>
    set((state) => {
      if (flowGeneratorSessionId && !state.chats.get(flowGeneratorSessionId)) {
        log(
          "Could not set active chat, session not found. This could be because vite reloaded the useChatStore",
          flowGeneratorSessionId,
          state.chats,
        );
      }

      return {
        activeChat: flowGeneratorSessionId ? Some(flowGeneratorSessionId) : None,
      };
    }),

  SetFlowGeneratorSession: (
    flowGeneratorSessionId: number,
    flowGeneratorSession: Partial<FlowGeneratorSession>,
  ) =>
    set((state) => {
      const existing = state.chats.get(flowGeneratorSessionId);
      if (!existing) return state;

      let session = flowGeneratorSession;

      if (session.flowGeneratorSession) {
        session.flowGeneratorSession.flowItems = session.flowGeneratorSession.flowItems.filter(
          (item) =>
            !existing.newMessages.some(
              (existingMessage) => existingMessage.flowItemId === item.flowItemId,
            ),
        );
      }

      const chats = new Map(state.chats);
      chats.set(flowGeneratorSessionId, {
        ...existing,
        flowGeneratorSession: {
          ...existing.flowGeneratorSession,
          ...session,
        } as FlowGeneratorSession,
      });
      return { chats };
    }),

  SetFlowGeneratorSessionIntervention: (
    flowGeneratorSessionId: number,
    flowGeneratorSessionIntervention: FlowGeneratorSessionIntervention,
  ) =>
    set((state) => {
      const existing = state.chats.get(flowGeneratorSessionId);
      if (!existing) return state;

      const chats = new Map(state.chats);
      chats.set(flowGeneratorSessionId, {
        ...existing,
        flowGeneratorSessionIntervention,
      });
      return { chats };
    }),

  SetStartupFinished: (flowGeneratorSessionId: number) =>
    set((state) => {
      const existing = state.chats.get(flowGeneratorSessionId);
      if (!existing) return state;

      const chats = new Map(state.chats);
      chats.set(flowGeneratorSessionId, {
        ...existing,
        isStartupFinished: true,
      });
      return { chats };
    }),

  SetSocratesState: (flowGeneratorSessionId: number, newState: PollingState) =>
    set((state) => {
      const existing = state.chats.get(flowGeneratorSessionId);
      if (!existing) return state;

      const chats = new Map(state.chats);
      chats.set(flowGeneratorSessionId, {
        ...existing,
        socrates: {
          session: existing.socrates.session,
          state: newState,
        },
      });
      return { chats };
    }),

  SetLlmMessage: (flowGeneratorSessionId: number, message: string | undefined) =>
    set((state) => {
      const existing = state.chats.get(flowGeneratorSessionId);
      if (!existing) return state;

      const chats = new Map(state.chats);
      chats.set(flowGeneratorSessionId, {
        ...existing,
        llmMessage: message,
      });
      return { chats };
    }),

  SetSocratesSession: (flowGeneratorSessionId: number, socratesSession: SocratesSession) =>
    set((state) => {
      const existing = state.chats.get(flowGeneratorSessionId);
      if (!existing) return state;

      const chats = new Map(state.chats);
      chats.set(flowGeneratorSessionId, {
        ...existing,
        socrates: {
          session: socratesSession,
          state: existing.socrates.state,
        },
      });
      return { chats };
    }),
  SetChatHasBeenOpened: (flowGeneratorSessionId: number) =>
    set((state) => {
      const existing = state.chats.get(flowGeneratorSessionId);
      if (!existing) return state;

      const chats = new Map(state.chats);
      chats.set(flowGeneratorSessionId, {
        ...existing,
        hasBeenOpened: true,
        lastOpened: new Date(),
      });

      return { chats };
    }),
  SoftResetChat: (flowGeneratorSessionId: number) =>
    set((state) => {
      // Invalidate any old query data so next open gets refreshed
      window.QueryClient.refetchQueries({
        queryKey: flowSessionQueryKey(flowGeneratorSessionId),
      }).catch((e) => {
        console.error("Failed to refetch query", e);
      });

      const existing = state.chats.get(flowGeneratorSessionId);
      if (!existing) return state;
      existing.removeHookCallbacks.forEach((cb) => cb.unwrapOr(() => {})());

      const chats = new Map(state.chats);

      chats.set(flowGeneratorSessionId, {
        ...existing,
        socrates: {
          session: undefined,
          state: PollingStates.Idle(),
        },
        isStartupFinished: false,
        // Keep the SAME array identity when there is nothing to unhook —
        // Chat.tsx has an effect that depends on removeHookCallbacks and calls
        // SoftResetChat while it's empty; a fresh [] every call re-triggers
        // that effect forever ("Maximum update depth exceeded")
        removeHookCallbacks:
          existing.removeHookCallbacks.length === 0 ? existing.removeHookCallbacks : [],
      });
      return { chats };
    }),
  SetFlaggedMessage: (flowGeneratorSessionId, held) =>
    set((state) => {
      const existing = state.chats.get(flowGeneratorSessionId);
      if (!existing) return state;

      const chats = new Map(state.chats);
      chats.set(flowGeneratorSessionId, {
        ...existing,
        flaggedMessage: {
          flagged: held,
          state: PollingStates.Ready(),
        },
      });

      return { chats };
    }),
  SetFlaggedMessageState: (flowGeneratorSessionId, polling) =>
    set((state) => {
      const existing = state.chats.get(flowGeneratorSessionId);
      if (!existing) return state;

      const chats = new Map(state.chats);
      chats.set(flowGeneratorSessionId, {
        ...existing,
        flaggedMessage: {
          ...existing.flaggedMessage,
          state: polling,
        },
      });

      return { chats };
    }),
}));

export const ChatFilter = (chat: Chat | undefined, filter: string): boolean => {
  if (filter.length === 0 || !chat) {
    return true;
  }

  const search = filter.toLowerCase();

  // prettier-ignore
  return (
    chat.flowGeneratorSessionId.toString().includes(search) ||
    (chat.flowGeneratorSession?.lessonType?.toLowerCase() ?? "").includes(
      search,
    ) ||
    (
      chat.flowGeneratorSession?.firstName?.toLowerCase() +
      " " +
      chat.flowGeneratorSession?.lastName?.toLowerCase()
    ).includes(search) ||
    (chat.flowGeneratorSession?.quizName?.toLowerCase().includes(search) ??
      false) ||
    chat.newMessages.filter((items) =>
      (items?.text?.toLowerCase() ?? "").includes(search),
    ).length > 0 ||
    (chat.flowGeneratorSession
      ? chat.flowGeneratorSession.flowGeneratorSession?.flowItems?.filter(
          (items) => items?.text?.toLowerCase().includes(search),
        ).length > 0
      : false)
  );
};
